import {uniq, uniqBy, countBy, toPairs} from "lodash-es";
import { Service, service, inject } from "./di";
import { LocationService } from "./LocationService";

@service
export default class CategoryBrowserService extends Service {
	@inject locationService: LocationService;

	fields = {
		url: "URL",
		product: "Product",
		category: "Google Category",
		subCategory: "Google SubCategory",
	};

	getCategories(products: any, location: any) {
		const categories = uniq(products.map((p: any) => p[this.fields.category]));
		return categories.map((category: any) => {
			const url = this.getCategoryUrl(products, category, location, true);
			return ({
				category,
				url,
				subCategories: this.getSubCategories(products, location, category, true),
			});
		});
	}

	getSubCategories(products: any, location: string, category: string, onlyTop3: boolean = false ) {
		const subCategories = products.filter((p: any) => onlyTop3 ? p[this.fields.category] === category : p[this.fields.url].includes(category));
		let uniqSubCategories = uniqBy(subCategories, this.fields.subCategory);
		if (onlyTop3) {
			const subCategoryByCount = countBy(subCategories, (r: any) => r[this.fields.subCategory]);
			const top3Categories = toPairs(subCategoryByCount)
				.sort((a: any, b: any) => b[1] - a[1])
				.slice(0, 3)
				.map(v => v[0]);
			uniqSubCategories = uniqSubCategories.filter(
				(c: any) => top3Categories.indexOf(c[this.fields.subCategory]) !== -1
			);
		}

		const currentUrl = this.getCategoryUrl(products, category, location, onlyTop3);
		return uniqSubCategories.map((category: any) => ({
			category: category[this.fields.subCategory],
			url: this.getSubCategoryUrl(category[this.fields.url], currentUrl)
		}));
	}

	prepareName = (name: string): string => name
		.split(" ")
		.map((w: string) => w === "&" ? "and" : w.toLowerCase())
		.join("-")

	getCategoryUrl = (products: any, categoryName: string, location: any, onlyTop3: boolean): string => {
		const categoryObject = products.find((p: any) => onlyTop3 ? p[this.fields.category] === categoryName :  p[this.fields.url].includes(categoryName));
		const uri = categoryObject[this.fields.url].split("/").splice(0, 2).join("/");

		return this.isLocationsBrowser() ?
			`${this.getLocaleCategorySuffix()}/${this.getLocationUrl(location)}${uri}` :
			`${this.getCategoryBrowserUrlStart()}${uri}`;
	}

	getSubCategoryUrl = (productUrl: string, currentUrl: string): string => {
		if (this.isLocationsBrowser()) {
			const urlSplitted = productUrl.split("/");
			urlSplitted.pop();

			const currentUrlSplitted = currentUrl.split("/");
			currentUrlSplitted.pop();

			return currentUrlSplitted.concat(urlSplitted).join("/").replace("//", "/");
		}

		const urlSplitted = productUrl.split("/");
		urlSplitted.pop();
		return this.getCategoryBrowserUrlStart() + urlSplitted.join("/");
	}

	getCategoryNameFromUrl = (products: any, categoryUrl: string): string => {
		const product = products.find((p: any) => p[this.fields.url].match(new RegExp(`^\/${categoryUrl}`)));
		return product[this.fields.category];
	}

	getSubCategoryNameFromUrl = (products: any, categoryUrl: string): string => {
		const product = products.find((p: any) => p[this.fields.url].match(new RegExp(`^\/${categoryUrl}`)));
		return product[this.fields.subCategory];
	}

	getLocaleCategorySuffix = () => `/${this.locationService.locale}`;

	getProducts(products: any, subCategory: string, locationState: string, locationCity: string, selectedCountry: string): any[] {
		return products
			.filter((p: any) => p[this.fields.subCategory] === subCategory)
			.map((p: any) => ({
				category: p[this.fields.product],
				url: this.getProductUrl(this.prepareName(p[this.fields.product]), locationState, locationCity, selectedCountry)
			}));
	}

	getProductUrl = (productName: string, state: string, city: string, selectedCountry: string): string => {
		if (this.isLocationsBrowser()) {
			const country = (selectedCountry === "en-us" || selectedCountry === "us") ? "us" : "ca";
			const lang = this.locationService.locale.split("-")[0];
			const locale = `${lang}-${country}`;

			return `/${locale}/search/${this.getInCityUrl(state, city)}/${productName}`;
		}

		return `/${this.locationService.locale}/products/${productName}/browser`;
	}

	getInCityUrl = (state: string, city: string) => `${this.getCityUrl(city)}-${state.toLowerCase()}`;

	getCityUrl = (city: string): string =>
		city
			.split(" ")
			.map((c: string) => c.toLowerCase())
			.join("-")

	getLocationUrl = (locationObject: any): string =>
		`browser/${locationObject["state"]}/${locationObject["city"]}`.toLowerCase()

	getCategoryBrowserUrlStart = () => `${this.locationService.locale}/categories`;

	isLocationsBrowser = () => window.location.pathname.includes("browser");
}
